import { useEffect, useState } from "react";
import { getSiteBanner, SiteBanner as SiteBannerData } from "../../services/SiteBannerService";
import styles from "./SiteBanner.module.css";

// localStorage key — persists dismissal across sessions until the message updates.
const DISMISS_KEY = "powerstats-banner-dismissed-at";

// Module-level cache so navigating between pages doesn't re-fetch.
// One in-flight promise is shared by all mounted instances.
let bannerPromise: Promise<SiteBannerData | null> | null = null;

const fetchBannerOnce = (): Promise<SiteBannerData | null> => {
    if (!bannerPromise) {
        bannerPromise = getSiteBanner()
            .then((b) => (b?.isActive && b.message ? b : null))
            .catch(() => null);
    }
    return bannerPromise;
};

export default function SiteBanner() {
    const [banner, setBanner] = useState<SiteBannerData | null>(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetchBannerOnce().then((b) => {
            if (cancelled || !b) return;
            setBanner(b);
            const dismissedAt = localStorage.getItem(DISMISS_KEY);
            if (dismissedAt && dismissedAt === b.updatedAt) {
                setDismissed(true);
            }
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const dismiss = () => {
        if (banner) localStorage.setItem(DISMISS_KEY, banner.updatedAt);
        setDismissed(true);
    };

    if (!banner || dismissed) return null;

    return (
        <div className={styles.banner} role="status">
            <span className={styles.message}>{banner.message}</span>
            <button
                type="button"
                className={styles.close}
                onClick={dismiss}
                aria-label="Dismiss"
            >
                ×
            </button>
        </div>
    );
}
